import {
  DEFAULT_LOCALE_SHORT,
  LOCALE_COOKIE_NAME,
  SHORT_LOCALES,
  parseShortLocale,
} from '../locales.constants'

const readQueryLocale = (search: string): SHORT_LOCALES | null => {
  if (!search) {
    return null
  }
  try {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
    return parseShortLocale(params.get('lang'))
  } catch {
    return null
  }
}

// Collect every paretos_lang cookie in the header, not just the first. RFC
// 6265 §5.4 only requires UAs to sort path-specific entries before path-root
// entries — so when a stale path-scoped write exists, "the first match" is the
// attacker-controlled one. If we see disagreement, distrust the lot and fall
// through to the next source rather than honour either.
const readCookieLocale = (cookieHeader: string): SHORT_LOCALES | null => {
  if (!cookieHeader) {
    return null
  }
  const hits = new Set<SHORT_LOCALES>()
  for (const part of cookieHeader.split(';')) {
    const [name, ...rest] = part.trim().split('=')
    if (name !== LOCALE_COOKIE_NAME) {
      continue
    }
    let parsed: SHORT_LOCALES | null
    try {
      parsed = parseShortLocale(decodeURIComponent(rest.join('=')))
    } catch {
      parsed = parseShortLocale(rest.join('='))
    }
    if (parsed) {
      hits.add(parsed)
    }
  }
  return hits.size === 1 ? [...hits][0] : null
}

const readAcceptLanguageLocale = (candidates: ReadonlyArray<string>): SHORT_LOCALES | null => {
  for (const c of candidates) {
    const hit = parseShortLocale(c)
    if (hit) {
      return hit
    }
  }
  return null
}

export interface ResolveSources {
  search?: string
  cookieHeader?: string
  injectedLocale?: string | null
  acceptLanguages?: ReadonlyArray<string>
}

// Priority: explicit ?lang= > persisted cookie > server-injected (Accept-Language
// resolved at first paint) > browser languages (dev/test fallback) > default.
export const resolveLocale = (sources: ResolveSources): SHORT_LOCALES => {
  const fromQuery = readQueryLocale(sources.search || '')
  if (fromQuery) {
    return fromQuery
  }

  const fromCookie = readCookieLocale(sources.cookieHeader || '')
  if (fromCookie) {
    return fromCookie
  }

  const fromInjected = parseShortLocale(sources.injectedLocale ?? null)
  if (fromInjected) {
    return fromInjected
  }

  const fromAccept = readAcceptLanguageLocale(sources.acceptLanguages || [])
  if (fromAccept) {
    return fromAccept
  }

  return DEFAULT_LOCALE_SHORT
}

// Convenience: read from the actual browser globals. Safe to call only after
// hydration — guards `window`/`document` so SSR-style calls don't throw.
export const resolveLocaleFromBrowser = (): SHORT_LOCALES => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return DEFAULT_LOCALE_SHORT
  }
  return resolveLocale({
    search: window.location.search,
    cookieHeader: document.cookie,
    injectedLocale: window.__PARETOS_LOCALE__,
    acceptLanguages: Array.isArray(navigator.languages)
      ? navigator.languages
      : [navigator.language].filter(Boolean),
  })
}
